const DEFAULT_HOME_FEED_COUNT = 5;
export const HOME_FEED_INCREMENT = 10;
export const MAX_HOME_FEED_COUNT = 100;

/** Query parameters controlling each independently paginated home-feed projection. */
export type HomeFeedCountParam = 'count' | 'personal_count' | 'social_count';

export const HOME_FEED_COUNT_PARAMS = [
  'count',
  'personal_count',
  'social_count',
] as const satisfies readonly HomeFeedCountParam[];

function cloneSearchParams(rawSearch: string): URLSearchParams {
  return new URLSearchParams(rawSearch);
}

export function normalizeHomeFeedCount(rawCount: string | null): number {
  if (!rawCount) {
    return DEFAULT_HOME_FEED_COUNT;
  }

  const parsed = Number(rawCount);

  if (!Number.isInteger(parsed) || parsed <= DEFAULT_HOME_FEED_COUNT) {
    return DEFAULT_HOME_FEED_COUNT;
  }

  return Math.min(parsed, MAX_HOME_FEED_COUNT);
}

export function buildCanonicalHomeFeedUrl(
  pathname: string,
  rawSearch: string,
  countParams: readonly HomeFeedCountParam[] = HOME_FEED_COUNT_PARAMS
): string {
  const params = cloneSearchParams(rawSearch);

  countParams.forEach((countParam) => {
    const normalizedCount = normalizeHomeFeedCount(params.get(countParam));

    if (normalizedCount === DEFAULT_HOME_FEED_COUNT) {
      params.delete(countParam);
    } else {
      params.set(countParam, String(normalizedCount));
    }
  });

  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function buildHomeFeedUrl(
  pathname: string,
  rawSearch: string,
  nextCount: number,
  countParam: HomeFeedCountParam = 'count'
): string {
  const params = cloneSearchParams(rawSearch);
  const normalizedNext = Math.min(
    Math.max(nextCount, DEFAULT_HOME_FEED_COUNT),
    MAX_HOME_FEED_COUNT
  );

  if (normalizedNext === DEFAULT_HOME_FEED_COUNT) {
    params.delete(countParam);
  } else {
    params.set(countParam, String(normalizedNext));
  }

  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}
